/* eslint-disable react/no-array-index-key */
import { FC, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { AutomationLogEntry } from '../../SharesTransfer/utils/automateDecreaseOperation';

const LOG_COLORS: Record<AutomationLogEntry['type'], string> = {
  error: '#ff6b6b',
  success: '#51cf66',
  info: '#adb5bd',
};

type LogWithId = AutomationLogEntry & { id: number };

type AutomationLogPanelProps = {
  logs: LogWithId[];
  isRunning?: boolean;
  onClear: () => void;
};

const AutomationLogPanel: FC<AutomationLogPanelProps> = ({
  logs,
  isRunning = false,
  onClear,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [logs]);

  if (logs.length === 0 && !isRunning) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '30%',
        minWidth: '280px',
        height: '100vh',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(0,0,0,0.9)',
        color: '#fff',
        fontFamily: 'monospace',
        fontSize: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #444',
        }}
      >
        <strong>Automation Log {isRunning && '(running...)'}</strong>
        <button
          type="button"
          onClick={onClear}
          style={{
            background: '#444',
            color: '#fff',
            border: 'none',
            padding: '4px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
          }}
        >
          Clear
        </button>
      </div>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        {logs.length === 0 && isRunning && (
          <div style={{ color: '#adb5bd' }}>Starting...</div>
        )}
        {logs.map((entry, index) => (
          <div
            key={`${index}-${entry.type}-${entry.msg}-${entry.step ?? ''}`}
            style={{ color: LOG_COLORS[entry.type] }}
          >
            {entry.msg}
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default AutomationLogPanel;
